// frontend/src/components/admin/DeviceList.jsx

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Loading from '../common/Loading';
import DeviceCard from './DeviceCard';
import DeviceDetails from './DeviceDetails';

const DeviceList = () => {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    online: 'all',
    search: '',
  });
  const [newDeviceForm, setNewDeviceForm] = useState({
    deviceId: '',
  });

  useEffect(() => {
    fetchDevices();
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [devices, filters]);

  const fetchDevices = async () => {
    try {
      const response = await api.get('/devices');
      console.log('📱 Devices récupérés:', response.data.data);
      setDevices(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/devices/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/devices/sync');
      
      const { inserted, modified } = response.data.data;
      
      let message = 'Synchronisation réussie!\n';
      if (inserted > 0) message += `${inserted} device(s) ajouté(s)\n`;
      if (modified > 0) message += `${modified} device(s) mis à jour\n`;
      
      alert(message);
      fetchDevices();
      fetchStats();
    } catch (error) {
      console.error('Erreur sync:', error);
      alert('Erreur lors de la synchronisation: ' + (error.response?.data?.message || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleAddManual = async (e) => {
    e.preventDefault();
    
    if (!newDeviceForm.deviceId.trim()) {
      alert('Veuillez entrer un Device ID');
      return;
    }
    
    try {
      const response = await api.post('/devices/add-device', {
        deviceId: newDeviceForm.deviceId.trim(),
      });
      
      alert(
        `Device ajouté avec succès !\n\nNom: ${response.data.data.name}\nType: ${response.data.data.type}\nEn ligne: ${response.data.data.online ? 'Oui' : 'Non'}`
      );
      
      setShowAddModal(false);
      setNewDeviceForm({ deviceId: '' });
      fetchDevices();
      fetchStats();
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Erreur lors de l'ajout";
      alert(`❌ ${errorMsg}`);
    }
  };

  const applyFilters = () => {
    let filtered = [...devices];

    if (filters.type !== 'all') {
      filtered = filtered.filter((d) => d.type === filters.type);
    }

    if (filters.online !== 'all') {
      const onlineValue = filters.online === 'online';
      filtered = filtered.filter((d) => d.online === onlineValue);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.name.toLowerCase().includes(searchLower) ||
          d.deviceId.toLowerCase().includes(searchLower) ||
          d.productName?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredDevices(filtered);
  };

  // ✅ MODIFICATION ICI : Passer le device complet avec _id
  const handleDeviceClick = (device) => {
    console.log('🔍 Device sélectionné:', device);
    console.log('🔍 Device _id:', device._id);
    setSelectedDevice(device);
  };

  if (loading) return <Loading />;

  if (selectedDevice) {
    return (
      <DeviceDetails
        device={selectedDevice}
        onClose={() => setSelectedDevice(null)}
        onUpdate={fetchDevices}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Devices Tuya</h1>
          <p className="text-gray-600 mt-1">
            Gestion de vos appareils connectés
          </p>
        </div>
        <div className="flex space-x-3">
          <Button onClick={() => setShowAddModal(true)} variant="secondary">
            + Ajouter manuellement
          </Button>
          <Button onClick={handleSync} disabled={syncing}>
            {syncing ? (
              <>
                <span className="animate-spin mr-2">⟳</span>
                Synchronisation...
              </>
            ) : (
              <>
                <span className="mr-2">🔄</span>
                Synchroniser
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <h3 className="text-sm font-semibold mb-2">Total Devices</h3>
            <p className="text-3xl font-bold">{stats.total}</p>
          </Card>

          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
            <h3 className="text-sm font-semibold mb-2">En ligne</h3>
            <p className="text-3xl font-bold">{stats.online}</p>
          </Card>

          <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <h3 className="text-sm font-semibold mb-2">Hors ligne</h3>
            <p className="text-3xl font-bold">{stats.offline}</p>
          </Card>

          <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <h3 className="text-sm font-semibold mb-2">Capteurs</h3>
            <p className="text-3xl font-bold">{stats.byType?.sensor || 0}</p>
            <p className="text-sm mt-1">Switches: {stats.byType?.switch || 0}</p>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              placeholder="Nom, ID..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            >
              <option value="all">Tous les types</option>
              <option value="sensor">Capteurs</option>
              <option value="switch">Switches</option>
              <option value="ir_controller">Contrôleurs IR</option>
              <option value="light">Lumières</option>
              <option value="other">Autres</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={filters.online}
              onChange={(e) =>
                setFilters({ ...filters, online: e.target.value })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
            >
              <option value="all">Tous</option>
              <option value="online">En ligne</option>
              <option value="offline">Hors ligne</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() =>
                setFilters({ type: 'all', online: 'all', search: '' })
              }
              className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </Card>

      {/* Liste des devices */}
      {filteredDevices.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDevices.map((device) => (
              <DeviceCard
                key={device._id}
                device={device}
                onClick={handleDeviceClick}
              />
            ))}
          </div>
          <div className="mt-4 text-sm text-gray-600 text-center">
            {filteredDevices.length} device(s) affiché(s) sur {devices.length}
          </div>
        </>
      ) : (
        <Card>
          <p className="text-center text-gray-500 py-8">
            {devices.length === 0
              ? 'Aucun device trouvé. Cliquez sur "Synchroniser" pour importer vos appareils.'
              : 'Aucun device ne correspond aux filtres.'}
          </p>
        </Card>
      )}

      {/* Modal Ajout Device */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Ajouter un nouveau device"
      >
        <form onSubmit={handleAddManual}>
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              Pour ajouter un nouveau device, entrez son ID Tuya. Le système
              récupérera automatiquement ses informations.
            </p>
            <Input
              label="Device ID Tuya"
              value={newDeviceForm.deviceId}
              onChange={(e) =>
                setNewDeviceForm({ deviceId: e.target.value })
              }
              placeholder="Exemple: bf00a36b3f5181fb62tkdn"
              required
            />
            <p className="text-xs text-gray-500 mt-2">
              💡 Vous pouvez trouver l'ID du device dans l'application Tuya
              Smart ou sur le portail développeur Tuya.
            </p>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="secondary"
              onClick={() => setShowAddModal(false)}
            >
              Annuler
            </Button>
            <Button type="submit">
              Ajouter le device
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DeviceList;